/**
 * RoomStore — the rooms feature's data-access wrapper.
 *
 * Strict layering: this is the ONLY rooms module that talks to the API, and it
 * does so exclusively through the {@link EngineeringVectorApi} facade
 * (`api.documents<Room>('rooms')` + `api.identityProvider`). It never imports
 * ApiClient or calls fetch. React hooks call this store; pages call the hook.
 *
 * Persistence model: each room is one document with id `room_<uuid>`. Queryable
 * metadata (name, description, createdBy, memberCount, defaultTemplateId,
 * createdAt, deleted) is mirrored onto the DDB pointer row's index attributes so
 * the list view renders without hydrating S3 bodies.
 *
 * Deletion: the API layer exposes no delete op, so {@link softDelete} sets a
 * `deleted` flag on the document and its pointer row; {@link listSummaries}
 * filters deleted rows out.
 */

import type { EngineeringVectorApi } from '../../api';
import type { DocumentStore } from '../../api';
import type { StringMap } from '../../api';
import type { Room, RoomMember, RoomSummary } from './types';
import { memberFromIdentity, roomDocId } from './roomModel';

export const ROOMS_COLLECTION = 'rooms';

/** Pointer-row index attribute keys. */
const ROW = {
  primaryIdentifier: 'primaryIdentifier',
  collection: 'collection',
  name: 'name',
  description: 'description',
  createdBy: 'createdBy',
  memberCount: 'memberCount',
  memberAliases: 'memberAliases',
  defaultTemplateId: 'defaultTemplateId',
  createdAt: 'createdAt',
  deleted: 'deleted',
} as const;

/** Fields the create form supplies; the store seeds the rest from identity. */
export type RoomDraft = Pick<Room, 'name' | 'description' | 'defaultTemplateId'>;

export class RoomStore {
  private readonly docs: DocumentStore<Room>;

  constructor(private readonly api: EngineeringVectorApi) {
    this.docs = api.documents<Room>(ROOMS_COLLECTION);
  }

  /** Builds the queryable index attributes mirrored onto the pointer row. */
  private indexAttributes(room: Room): StringMap {
    return {
      [ROW.name]: room.name,
      [ROW.description]: room.description ?? '',
      [ROW.createdBy]: room.createdBy ?? '',
      [ROW.memberCount]: String(room.members.length),
      [ROW.memberAliases]: this.aliasesString(room),
      [ROW.defaultTemplateId]: room.defaultTemplateId ?? '',
      [ROW.createdAt]: room.createdAt ?? '',
      [ROW.deleted]: room.deleted ? 'true' : 'false',
    };
  }

  /** Lowercased, de-duped, comma-joined member aliases for the pointer row. */
  private aliasesString(room: Room): string {
    const seen = new Set<string>();
    for (const m of room.members) {
      const a = m.alias.trim().toLowerCase();
      if (a) seen.add(a);
    }
    return Array.from(seen).join(',');
  }

  /** Projects a raw pointer row into a lightweight summary. */
  private toSummary(row: StringMap): RoomSummary {
    return {
      id: row[ROW.primaryIdentifier] ?? '',
      name: row[ROW.name] ?? '',
      description: row[ROW.description] ?? '',
      createdBy: row[ROW.createdBy] ?? '',
      memberCount: Number(row[ROW.memberCount] ?? '0') || 0,
      memberAliases: (row[ROW.memberAliases] ?? '')
        .split(',')
        .map((a) => a.trim().toLowerCase())
        .filter(Boolean),
      defaultTemplateId: row[ROW.defaultTemplateId] ?? '',
      createdAt: row[ROW.createdAt] ?? '',
    };
  }

  /**
   * Lists non-deleted room summaries. Filters to this collection defensively
   * (the underlying list is not collection-scoped).
   */
  async listSummaries(): Promise<RoomSummary[]> {
    const rows = await this.docs.list();
    return rows
      .filter(
        (r) =>
          r[ROW.collection] === ROOMS_COLLECTION &&
          r[ROW.deleted] !== 'true' &&
          !!r[ROW.primaryIdentifier],
      )
      .map((r) => this.toSummary(r));
  }

  /** Loads a single room document by id. */
  async get(id: string): Promise<Room | undefined> {
    return this.docs.get(id);
  }

  /**
   * Creates a new room. Seeds createdBy and the initial owner member from the
   * Midway identity, stamps createdAt, and returns the saved document.
   */
  async create(draft: RoomDraft): Promise<Room> {
    const identity = await this.api.identityProvider.getIdentity();
    const room: Room = {
      id: roomDocId(),
      name: draft.name.trim(),
      description: draft.description ?? '',
      createdBy: identity.alias,
      members: [memberFromIdentity(identity, 'owner')],
      defaultTemplateId: draft.defaultTemplateId || undefined,
      createdAt: new Date().toISOString(),
      deleted: false,
    };
    await this.docs.save(room.id, room, this.indexAttributes(room));
    return room;
  }

  /**
   * Updates an existing room document in place (same id). Re-mirrors index
   * attributes and clears any stale deleted flag.
   */
  async update(room: Room): Promise<Room> {
    const updated: Room = { ...room, deleted: false };
    await this.docs.update(updated.id, updated, this.indexAttributes(updated));
    return updated;
  }

  /**
   * Adds members to an existing room, skipping aliases that are already present
   * (existing members — and their roles, e.g. owner — are preserved). Re-saves
   * the room so the pointer row's mirrored `memberAliases` reflects the change.
   * No-ops (no write) when there is nothing new to add or the room is
   * missing/deleted. Returns the current room document, or undefined if absent.
   */
  async addMembers(
    id: string,
    incoming: RoomMember[],
  ): Promise<Room | undefined> {
    const existing = await this.docs.get(id);
    if (!existing || existing.deleted) return undefined;

    const present = new Set(
      existing.members.map((m) => m.alias.trim().toLowerCase()),
    );
    const additions = incoming.filter((m) => {
      const a = m.alias.trim().toLowerCase();
      return a.length > 0 && !present.has(a);
    });
    if (additions.length === 0) return existing;

    const updated: Room = {
      ...existing,
      members: [...existing.members, ...additions],
      deleted: false,
    };
    await this.docs.update(updated.id, updated, this.indexAttributes(updated));
    return updated;
  }

  /**
   * Soft-deletes a room: fetches it, flags it deleted, and updates both the S3
   * body and the pointer row so list() filters it out.
   */
  async softDelete(id: string): Promise<void> {
    const existing = await this.docs.get(id);
    if (!existing) return;
    const deleted: Room = { ...existing, deleted: true };
    await this.docs.update(id, deleted, this.indexAttributes(deleted));
  }
}
